<template>
  <div class="station-view">
    <transition name="slide-fade">
      <div class="station-search-view">

        <div class="station-scanner-entry my-4">
          <h2 class="text-muted">Search for order details or enter panel barcode</h2>
          <FormInputText ref="searchInput" v-model="searchText" @click="$refs.searchInput.select()" placeholder="Search" prependSlot>
            <template v-slot:prepend>
              <i class="far fa-search"></i>
            </template>
          </FormInputText>
        </div>
        <div class="station-list-container">
          <div class="station-list station-search-list" ref="stationList">
            <Spinner v-if="searchLoading" />
            <div v-for="searchCategory of searchResults" :key="searchCategory.linkType">
              <div>
                <ExpandCollapseButton class="float-left mt-2 mr-2" v-model="searchCategory.isExpanded" /><h1 class="my-0">{{searchCategory.name}}</h1>
              </div>

              <div v-if="searchCategory.isExpanded">
                <div v-if="searchCategory.linkType == LinkTypes.productionPanel">
                  <StationSearchRow
                    :title="`${panel.barcode}`"
                    icon="fa-layer-group"
                    @click="panelClicked(panel)"
                    v-for="panel of searchCategory.results" :key="`panel-${panel.id}`">

                    <template v-slot:notes>
                      <StationSearchRowNote icon="fa-inventory">{{panel.orderItem.stock.name}}</StationSearchRowNote>
                      <StationSearchRowNote icon="fa-ruler-combined">{{panel.panelHeight}} x {{panel.panelWidth}}</StationSearchRowNote>
                      <StationSearchRowNote icon="fa-user-circle">{{panel.order.contact.name}}</StationSearchRowNote>
                      <StationSearchRowNote icon="fa-shopping-cart">{{panel.order.sequenceNumber}}</StationSearchRowNote>
                      <StationSearchRowNote icon="fa-tag">{{formatHyphenEmptyValue(panel.order.customerRef)}}</StationSearchRowNote>
                    </template>

                    <template v-slot:labels>
                      <LabelBadge v-for="(tag, index) of panel.stageHighlightTags" :key="`tag-${index}`" :labelColour="tag.colour" class="text-uppercase">{{tag.name}}</LabelBadge>

                      <LabelBadge labelClass="label-order-hold" v-if="panel.order.isOnHold">ON HOLD</LabelBadge>                
                      <LabelBadge labelClass="label-order-urgent" v-if="panel.order.isUrgent">Urgent</LabelBadge>
                      <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="panel.isRecut">RECUT</LabelBadge>
                      <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="panel.order.type == OrderTypes.nonChargeableRework">NCR</LabelBadge>
                      <LabelBadge labelClass="label-production-status-recut" v-if="panel.status == ProductionPanelStatusTypes.recut">STOP: RECUT</LabelBadge>
                      <LabelBadge labelClass="label-order-status-denied" v-if="panel.status == ProductionPanelStatusTypes.cancelled">STOP: CANCELLED</LabelBadge>
                      <LabelBadge labelClass="label-order-template-required" v-if="panel.orderItem.isTemplateHandlingRequired">TEMPLATE REQUIRED</LabelBadge>
                    </template>                
                  </StationSearchRow>
                </div>

                <div v-if="searchCategory.linkType == LinkTypes.order">

                  <StationSearchRow
                    :title="`${order.sequenceNumber} - ${order.contact.name}`"
                    icon="fa-shopping-cart"
                    @click="orderClicked(order)"
                    v-for="order of searchCategory.results" :key="`order-${order.id}`">

                    <template v-slot:notes>
                      <StationSearchRowNote icon="fa-tag">{{formatHyphenEmptyValue(order.customerRef)}}</StationSearchRowNote>
                      <StationSearchRowNote icon="fa-clock">{{formatDate(order.dateCurrentlyDue)}}</StationSearchRowNote>
                    </template>

                    <template v-slot:labels>
                      <LabelBadge labelClass="label-order-urgent" v-if="order.isUrgent">Urgent</LabelBadge>
                      <LabelBadge labelClass="label-order-type-nonChargeableRework" v-if="order.type == OrderTypes.nonChargeableRework">NCR</LabelBadge>
                      <LabelBadge labelClass="label-order-hold" v-if="order.isOnHold">ON HOLD</LabelBadge>
                      <LabelOrderStatusBadge :status="order.status" />
                    </template>                
                  </StationSearchRow>
                </div>

                <div v-if="searchCategory.linkType == LinkTypes.glassLevel">

                  <StationSearchRow
                    :title="`Offcut - ${glassLevel.barcode}`"
                    icon="fa-layer-group"
                    @click="offcutClicked(glassLevel)"
                    v-for="glassLevel of searchCategory.results" :key="`glassLevel-${glassLevel.id}`">

                    <template v-slot:notes>
                      <StationSearchRowNote icon="fa-inventory">{{glassLevel.stock.name}}</StationSearchRowNote>
                      <StationSearchRowNote icon="fa-ruler-combined">{{glassLevel.height}} x {{glassLevel.width}}</StationSearchRowNote>
                    </template>

                    <template v-slot:labels>
                      <LabelBadge labelClass="label-inventory-offcut">OFFCUT</LabelBadge>
                    </template>                
                  </StationSearchRow>
                </div>
              </div>
                

              <h3 class="text-center text-muted mt-4">{{searchCategory.results.length}} of {{searchCategory.totalResults}} {{searchCategory.name}} {{searchCategory.isExpanded ? 'Shown' : 'Hidden'}}</h3>
            </div>
            
            <h2 class="text-center text-danger mt-4" v-if="searchError">{{searchError}}</h2>
            <h2 class="text-center text-muted mt-4" v-if="searchResults.length == 0 && !searchError && !searchLoading && searchText">No results found for &quot;{{searchText}}&quot;</h2>
          </div>
        </div>
        <SchedulePanelPopover
          ref="panelPopover"
          @orderClick="displayPanelOrder($event)"
          @orderItemsClick="displayPanelOrderItems($event)"
          @orderDocumentsClick="displayPanelOrderDocuments($event)"
          @orderEditorViewClick="displayPanelOrderEditorView($event)"
          @printLabelClick="printPanelLabel($event)"
          @reprintLabelClick="reprintPanelLabel($event)"
          @recutClick="displayPanelRecut($event, productionStationId)"
          @revertClick="displayPanelRevert($event, productionStationId)"
          />

        <ScheduleOrderPopover
          ref="orderPopover"
          @orderClick="displayOrder($event)"
          @orderItemsClick="displayOrderOrderItems($event)"
          @orderDocumentsClick="displayOrderDocuments($event)"
          />
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.station-search-view {
  flex: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items:stretch;
  overflow-x: hidden;
}
.station-search-list {
  width: 70%;
  margin-left: 15%;
  font-size: 13px;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import EventBus from '@/utils/eventBus'
import Utils from '@/utils/utils'

import productionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'
import barcodeMixin from '@/mixins/barcode'
import StationPanelScheduleList from './../components/schedule/stationPanelScheduleList'
import SchedulePanelPopover from './../components/schedule/schedulePanelPopover'
import ScheduleOrderPopover from './../components/schedule/scheduleOrderPopover'
import StationScheduleRow from '@/apps/glass-production-station/src/components/schedule/stationScheduleRow'
import StationOrderScheduleRow from '@/apps/glass-production-station/src/components/schedule/stationOrderScheduleRow'
import GlassOffcutStationDialog from '@/apps/glass-production-station/src/views/glassOffcutStationDialog'
import StationFilterButton from '@/apps/glass-production-station/src/components/station/stationFilterButton'
import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'
import LabelPanelStatusBadge from '@/modules/production/components/labelPanelStatusBadge'

import StationSearchRow from '@/apps/glass-production-station/src/components/station/stationSearchRow'
import StationSearchRowNote from '@/apps/glass-production-station/src/components/station/stationSearchRowNote'

import { ProductionPanelStatusTypes, ProductionPanelStatusTypeLookup, DespatchTypeLookup, OrderTypes, OrderStatusTypes, LinkTypes } from '@/store/types'

export default {
  name: 'StationSearchPageView',
  mixins: [
    productionStationMixin,
    barcodeMixin
  ],
  components: {
    StationPanelScheduleList,
    SchedulePanelPopover,
    ScheduleOrderPopover,
    StationScheduleRow,
    StationOrderScheduleRow,
    StationFilterButton,
    LabelOrderStatusBadge,
    LabelPanelStatusBadge,
    StationSearchRow,
    StationSearchRowNote
  },
  props: {
    productionStageId: Number,
    productionStationId: Number
  },
  data() {
    return {
      searchLoading: false,
      searchText: '',
      searchError: '',

      searchResults: []
    }
  },
  computed: {
  },
  watch: {
    searchText: function() {
      this.searchLoading = true
      this.debouncedSearch()
    },
  },
  created() {
    this.DespatchTypeLookup = DespatchTypeLookup
    this.OrderStatusTypes = OrderStatusTypes
    this.ProductionPanelStatusTypes = ProductionPanelStatusTypes
    this.LinkTypes = LinkTypes
    this.OrderTypes = OrderTypes

    this.debouncedSearch = _.debounce(this.requestSearchInfo, 250)
  },
  mounted() {
    EventBus.on(EventBus.EventTypes.panelRecut, this.onPanelRecutComplete)
    this.$refs.searchInput.focus()
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.panelRecut, this.onPanelRecutComplete)
  },
  methods: {
    requestSearchInfo() {
      this.searchError = ''

      if(this.$data.searchText == '') {
        this.searchLoading = false
        this.searchResults = []
        return
      }

      this.searchLoading = true

      this.$apiService.glassProductionStationService.performStationAppSearch(this.productionStationId, { searchText: this.searchText, isProductionOnly: true })
      .then(response => {
        this.searchResults = response.data.categories.map(category => {
          category.isExpanded = true

          return category
        })
      })
      .catch(err => {
        this.searchError = 'Error loading search results'
      })
      .finally(() => {
        this.searchLoading = false
      })
    },
    orderClicked(evt) {
      this.$refs.orderPopover.displayFromElement(this.$refs.stationList, evt)      
    },
    panelClicked(evt) {
      this.$refs.panelPopover.displayFromElement(this.$refs.stationList, evt)      
    },
    offcutClicked(evt) {
      this.displayModal(GlassOffcutStationDialog, {
        id: evt.id,
        productionStageId: this.productionStageId,
        productionStationId: this.productionStationId
      })
      .then(() => {
      })
    },
    onPanelRecutComplete() {
      this.requestSearchInfo()
    },
    focusSearchEntry() {
      this.$refs.searchInput.focus()
    }
  }
}
</script>